import { useRef } from 'react';

import { Box as Flex, Tooltip } from '@material-ui/core';

import {
  RiDeleteBinLine,
  RiImageAddLine,
  RiImageEditLine,
  RiImageLine,
} from '@remixicon/react';
import { Node, mergeAttributes } from '@tiptap/core';
import {
  NodeViewContent,
  NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react';
import classNames from 'classnames';

import {
  CommentZone,
  CommentZoneProvider,
} from '@spotify/plugin-confidence-comments-react';

import {
  useBlockZoneId,
  useCardDelete,
  useCardFocusTracking,
  useImageUpload,
  useIsEditorEditable,
} from '../../hooks';
import { blockIdAttribute, generateBlockId } from '../../utils';
import { BlockReorderButtons } from '../BlockReorderButtons';
import { BlockCommentButton } from '../Comment';
import { ToolbarCard } from '../FloatingCard';
import { ToolbarButton } from '../ToolbarButton';
import { CARD_CONTENT } from '../blockTypes';
import { useImageCardStyles } from './imageCardStyles';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    imageCard: {
      setImageCard: () => ReturnType;
    };
  }
}

const ImageCardComponent = ({
  node,
  updateAttributes,
  editor,
  getPos,
}: NodeViewProps) => {
  const classes = useImageCardStyles();
  const isEditable = useIsEditorEditable(editor);
  const cardRef = useRef<HTMLDivElement>(null);
  const isFocused = useCardFocusTracking(editor, getPos, node.nodeSize);
  const { fileInputRef, openFileDialog, handleFileSelect } =
    useImageUpload(updateAttributes);
  const handleDelete = useCardDelete(editor, getPos, node.nodeSize);
  const { src, blockId } = node.attrs;
  const zoneId = useBlockZoneId('image-card', blockId, updateAttributes);

  const renderImage = () => {
    if (!isEditable) {
      return src ? (
        <img src={src} alt="Uploaded content" className={classes.image} />
      ) : (
        <div className={classes.imagePlaceholderStatic}>
          <RiImageLine size={32} />
          <span>No image</span>
        </div>
      );
    }

    return (
      <>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className={classes.hiddenFileInput}
        />
        {src ? (
          <button
            type="button"
            onClick={openFileDialog}
            className={classes.imageButton}
            aria-label="Change image"
          >
            <img src={src} alt="Uploaded content" className={classes.image} />
            <div className={classes.imageOverlay} data-image-overlay>
              <RiImageEditLine size={32} />
            </div>
          </button>
        ) : (
          <button
            type="button"
            className={classes.imagePlaceholder}
            onClick={openFileDialog}
          >
            <RiImageAddLine size={32} />
            <span style={{ marginTop: 8 }}>Click to upload an image</span>
          </button>
        )}
      </>
    );
  };

  const commentZoneId = blockId ? `image-card/${blockId}` : undefined;

  const content = (
    <div
      ref={cardRef}
      className={classNames(classes.card, { [classes.cardFocused]: isFocused })}
    >
      {isEditable && (
        <div contentEditable={false} className={classes.hoverToolbar}>
          <ToolbarCard>
            <Flex
              display="flex"
              style={{
                gap: 'var(--bui-space-0_5)',
              }}
            >
              <BlockReorderButtons getPos={getPos} editor={editor} />
              {commentZoneId && (
                <CommentZoneProvider zone={commentZoneId}>
                  <BlockCommentButton />
                </CommentZoneProvider>
              )}
              <Tooltip title="Delete card">
                <ToolbarButton aria-label="Delete card" onClick={handleDelete}>
                  <RiDeleteBinLine />
                </ToolbarButton>
              </Tooltip>
            </Flex>
          </ToolbarCard>
        </div>
      )}
      <div className={classes.imageContainer}>{renderImage()}</div>
      <div className={classes.contentContainer}>
        <NodeViewContent />
      </div>
    </div>
  );

  return (
    <NodeViewWrapper>
      {zoneId ? (
        <CommentZone id={zoneId} component="div">
          {content}
        </CommentZone>
      ) : (
        content
      )}
    </NodeViewWrapper>
  );
};

export const ImageCard = Node.create({
  name: 'imageCard',
  group: 'block',
  content: CARD_CONTENT,
  isolating: true,

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: element => element.getAttribute('data-src'),
        renderHTML: attributes => ({
          'data-src': attributes.src,
        }),
      },
      blockId: blockIdAttribute,
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="image-card"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'image-card' }),
      0,
    ];
  },

  addNodeView() {
    // eslint-disable-next-line new-cap
    return ReactNodeViewRenderer(ImageCardComponent);
  },

  addCommands() {
    return {
      setImageCard:
        () =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { blockId: generateBlockId() },
            content: [
              {
                type: 'heading',
                attrs: { level: 3 },
                content: [{ type: 'text', text: 'Add card title...' }],
              },
              {
                type: 'paragraph',
                content: [{ type: 'text', text: 'Add description...' }],
              },
              {
                type: 'tag',
                content: [{ type: 'text', text: 'Add tag text...' }],
              },
            ],
          });
        },
    };
  },
});
